import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { ClipboardList, Calendar, FileText, Printer, MapPin, Package, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import api, { API_URL } from "../lib/api";
import { toast } from "sonner";

// 3PA lifecycle columns (in order)
const COLUMNS = [
  { id: "AGENT", title: "Assigned", hint: "Waiting for scheduling" },
  { id: "SCHEDULED", title: "Scheduled", hint: "Collection booked" },
  { id: "IN_TRANSIT", title: "In Transit", hint: "On the road" },
  { id: "JHB_DEPOT", title: "Delivered to JHB", hint: "Drop here to hand over" },
];

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

function hoursSince(iso) {
  if (!iso) return 0;
  try { return (Date.now() - new Date(iso).getTime()) / 3600000; } catch { return 0; }
}

export default function ThirdPartyJobsBoard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // 3PA / EXTERNAL agents hand off at JHB_DEPOT — that card leaves their
  // scope. CS staff keep operating through JHB_DEPOT and beyond, so the
  // card stays visible on their board for their last-mile workflow.
  const isHandoffRole = user?.role === "3PA" || user?.role === "EXTERNAL";
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [scheduleDialog, setScheduleDialog] = useState(null); // { jobId } when SCHEDULE needs input

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/job-cards");
      // Show every lifecycle status the user's role can act on. For 3PA /
      // EXTERNAL that's AGENT → SCHEDULED → IN_TRANSIT (handoff at depot).
      // For CS it includes JHB_DEPOT too (local-delivery last-mile).
      const visible = isHandoffRole
        ? ["AGENT", "SCHEDULED", "IN_TRANSIT"]
        : ["AGENT", "SCHEDULED", "IN_TRANSIT", "JHB_DEPOT"];
      const list = (data.items || []).filter((j) => visible.includes(j.status));
      setJobs(list);
    } catch (e) {
      toast.error("Failed to load jobs");
    } finally { setLoading(false); }
  }, [isHandoffRole]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => {
    const int = setInterval(loadJobs, 60000);
    return () => clearInterval(int);
  }, [loadJobs]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) return;

    const job = jobs.find((j) => j.id === draggableId);
    const target = destination.droppableId;

    // Need to collect date + ref if moving AGENT -> SCHEDULED and they aren't on the card yet
    if (target === "SCHEDULED" && (!job?.estimated_collection_date || !job?.tpa_reference_number)) {
      setScheduleDialog({ jobId: draggableId });
      return;
    }

    // Optimistic UI: move locally
    setJobs((prev) =>
      prev.map((j) => (j.id === draggableId ? { ...j, status: target } : j))
    );

    try {
      await api.put(`/job-cards/${draggableId}/kanban-status`, { status: target });
      toast.success(`Moved to ${COLUMNS.find((c) => c.id === target)?.title}`);
      if (target === "JHB_DEPOT" && isHandoffRole) {
        // For 3PA / EXTERNAL the card hands off at the depot and leaves
        // their scope. CS continues to operate on it.
        setJobs((prev) => prev.filter((j) => j.id !== draggableId));
      } else {
        loadJobs();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
      loadJobs();
    }
  };

  const filteredJobs = jobs.filter((j) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (j.job_card_number || "").toLowerCase().includes(q) ||
      (j.model || "").toLowerCase().includes(q) ||
      (j.store || "").toLowerCase().includes(q) ||
      (j.area || "").toLowerCase().includes(q)
    );
  });

  const cardsByColumn = {};
  COLUMNS.forEach((c) => { cardsByColumn[c.id] = []; });
  filteredJobs.forEach((j) => {
    if (cardsByColumn[j.status]) cardsByColumn[j.status].push(j);
  });

  return (
    <div className="p-4 lg:p-6 max-w-full">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ClipboardList size={22} className="text-teal-600" />
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-800" data-testid="page-title">
              All Jobs
            </h1>
            <p className="text-xs text-slate-500">Drag cards between columns to update status. Drop into "Delivered to JHB" to hand over.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="3pa-jobs-search"
            placeholder="Search JC, model, store…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs w-56"
          />
          <Button
            data-testid="3pa-jobs-refresh"
            variant="outline"
            size="sm"
            onClick={loadJobs}
            className="text-xs"
          >
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-12 text-center">Loading jobs…</div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-2 items-start" data-testid="kanban-3pa">
            {COLUMNS.map((col) => {
              const colCards = cardsByColumn[col.id] || [];
              return (
                <div
                  key={col.id}
                  className="min-w-[280px] max-w-[320px] flex-1 bg-slate-50 rounded-xl border border-slate-200 flex flex-col"
                >
                  <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm text-slate-700" data-testid={`3pa-col-${col.id}`}>
                        {col.title}
                      </h3>
                      <span className="text-[10px] bg-white text-slate-500 rounded-full px-2 py-0.5 border border-slate-200">
                        {colCards.length}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400">{col.hint}</span>
                  </div>

                  <Droppable droppableId={col.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 p-2 min-h-[200px] space-y-2 transition-colors ${snapshot.isDraggingOver ? "bg-teal-50" : ""}`}
                      >
                        {colCards.length === 0 && !snapshot.isDraggingOver && (
                          <p className="text-[11px] text-slate-400 text-center py-6">
                            Drop cards here
                          </p>
                        )}
                        {colCards.map((job, idx) => (
                          <Draggable key={job.id} draggableId={job.id} index={idx}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                data-testid={`3pa-card-${job.id}`}
                                onClick={() => navigate(`/jobs/${job.id}`)}
                                className={`bg-white rounded-lg border border-slate-200 p-3 cursor-pointer hover:border-teal-400 hover:shadow-sm transition-all ${snapshot.isDragging ? "shadow-lg ring-2 ring-teal-400" : ""}`}
                              >
                                <h4 className="text-sm font-semibold text-teal-700 leading-tight mb-1" data-testid={`3pa-card-jc-${job.id}`}>
                                  {job.job_card_number || "-"}
                                </h4>
                                {job.job_type && (
                                  <span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-medium mb-1 ${
                                    job.job_type === "DELIVERY"
                                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                                      : "bg-purple-50 text-purple-700 border border-purple-200"
                                  }`}>{job.job_type}</span>
                                )}
                                {job.status === "AGENT" && !job.tpa_reference_number && hoursSince(job.date_allocated || job.date_created) > 24 && (
                                  <div
                                    data-testid={`3pa-warn-${job.id}`}
                                    className="mt-1 mb-1 flex items-start gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-md text-[10px] text-amber-800"
                                  >
                                    <AlertCircle size={11} className="flex-shrink-0 mt-0.5" />
                                    <span>Missing 3PA Ref &mdash; drag to Scheduled or click to add</span>
                                  </div>
                                )}
                                <div className="flex items-start gap-1.5 text-xs text-slate-600">
                                  <Package size={11} className="mt-0.5 text-slate-400 flex-shrink-0" />
                                  <span className="line-clamp-1">{job.model || "-"}</span>
                                </div>
                                <div className="flex items-start gap-1.5 text-xs text-slate-500 mt-1">
                                  <MapPin size={11} className="mt-0.5 text-slate-400 flex-shrink-0" />
                                  <span className="line-clamp-1">{job.store || "-"} · {job.area || "-"}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-400">
                                  <Calendar size={10} /> Created {formatDate(job.date_created)}
                                </div>
                                {job.tpa_reference_number && (
                                  <p className="text-[10px] text-slate-500 mt-1">
                                    Ref: <span className="font-medium text-slate-700">{job.tpa_reference_number}</span>
                                  </p>
                                )}
                                {job.estimated_collection_date && (
                                  <p className="text-[10px] text-slate-500">
                                    Collect: <span className="font-medium text-slate-700">{formatDate(job.estimated_collection_date)}</span>
                                  </p>
                                )}
                                {job.pdf_url && (
                                  <div className="flex gap-1 mt-2 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                                    <a
                                      href={`${API_URL}/api/files/${job.pdf_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      data-testid={`3pa-card-print-${job.id}`}
                                      className="inline-flex items-center gap-1 text-[10px] text-teal-600 hover:text-teal-800 font-medium"
                                    >
                                      <Printer size={10} /> Print JC
                                    </a>
                                    <a
                                      href={`${API_URL}/api/files/${job.pdf_url}`}
                                      download={job.original_filename || `${job.job_card_number}.pdf`}
                                      data-testid={`3pa-card-download-${job.id}`}
                                      className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-800 font-medium ml-2"
                                    >
                                      <FileText size={10} /> PDF
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {scheduleDialog && (
        <ScheduleDialog
          jobId={scheduleDialog.jobId}
          onClose={() => setScheduleDialog(null)}
          onDone={() => { setScheduleDialog(null); loadJobs(); }}
        />
      )}
    </div>
  );
}

function ScheduleDialog({ jobId, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!date || !ref.trim()) {
      toast.error("Both date and reference are required");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/job-cards/${jobId}/kanban-status`, {
        status: "SCHEDULED",
        estimated_collection_date: date,
        reference_number: ref.trim(),
      });
      toast.success("Scheduled");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="3pa-schedule-dialog">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5">
        <h3 className="font-semibold text-slate-800 mb-1">Schedule Collection</h3>
        <p className="text-xs text-slate-500 mb-4">
          To move this card to Scheduled, enter the collection date and your internal reference number.
        </p>
        <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Collection Date</label>
        <Input
          data-testid="3pa-schedule-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="mt-1 mb-3 h-9 text-sm"
        />
        <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Reference Number</label>
        <Input
          data-testid="3pa-schedule-ref"
          placeholder="Your internal ref (e.g., AG-2024-001)"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          className="mt-1 mb-4 h-9 text-sm"
        />
        <div className="flex justify-end gap-2">
          <Button
            data-testid="3pa-schedule-cancel"
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-xs h-8"
          >
            Cancel
          </Button>
          <Button
            data-testid="3pa-schedule-save"
            onClick={submit}
            disabled={saving}
            size="sm"
            className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-8"
          >
            {saving ? "Scheduling…" : "Schedule"}
          </Button>
        </div>
      </div>
    </div>
  );
}
